/**
 * RewardReveal — 可変報酬（フックモデル）のフロント接続 + 描画
 *
 * 1. マウント時に GET /api/user/rewards/batch で未表示報酬を取得し、
 *    GamenicsManager.triggerVariableReward でスロット演出を起動する。
 * 2. 演出が発行する `gamenics:reward-reveal` イベントを購読し、
 *    画面右下にトーストとして段階表示する。
 *
 * デザイン: ライト×ポップ（サーフェス＋インク枠＋ハードシャドウ、SVGアイコン、脱絵文字）。
 * フェイルセーフ: 取得・描画いずれの失敗でもアプリ動作を阻害しない。
 *
 * @see services/gamenics.ts triggerVariableReward()
 * @see workers/anokoe/src/routes/gamenics.ts GET /rewards/batch
 */

import React, { useEffect, useState } from 'react';
import { apiFetch } from '../services/api';
import { GamenicsManager } from '../services/gamenics';
import { Icon, IconName } from './icons/Icon';

interface RewardToast {
  id: number;
  type: string;
  data: { count?: number; from_user?: string; post_id?: string } | Record<string, unknown>;
}

function safeParse(value: unknown): Record<string, unknown> {
  try {
    if (typeof value === 'string') return JSON.parse(value);
    if (value && typeof value === 'object') return value as Record<string, unknown>;
    return {};
  } catch {
    return {};
  }
}

// 報酬タイプ → アイコン / 文言 / アイコン枠の配色（デザインシステム準拠）
function presentFor(toast: RewardToast): { icon: IconName; text: string; badgeCls: string } {
  if (toast.type === 'like') {
    const count = Number((toast.data as { count?: number }).count) || 1;
    return { icon: 'heart', text: `${count}件の「いいね」が届きました！`, badgeCls: 'bg-pop text-white' };
  }
  if (toast.type === 'badge') {
    return { icon: 'award', text: '新しいバッジを獲得！', badgeCls: 'bg-sun text-ink' };
  }
  if (toast.type === 'ai_praise') {
    return { icon: 'star', text: 'AIからの称賛が届きました！', badgeCls: 'bg-primary text-white' };
  }
  if (toast.type === 'milestone') {
    return { icon: 'star', text: 'マイルストーン達成！', badgeCls: 'bg-role-voice text-white' };
  }
  return { icon: 'gift', text: '報酬を獲得しました！', badgeCls: 'bg-ink text-sun' };
}

export const RewardReveal: React.FC = () => {
  const [toasts, setToasts] = useState<RewardToast[]>([]);

  // 1. 未表示の可変報酬を取得 → 演出をトリガー
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/user/rewards/batch');
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const rows = (json && json.data) || [];
        if (cancelled || !Array.isArray(rows) || rows.length === 0) return;
        const rewards = rows.map((r: { reward_type: string; reward_data: unknown }) => ({
          type: r.reward_type,
          data: safeParse(r.reward_data),
        }));
        GamenicsManager.triggerVariableReward(rewards);
      } catch (e) {
        console.warn('[RewardReveal] reward fetch failed:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. 段階表示イベントを購読してトースト描画
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent<{ type: string; data: unknown }>).detail;
        if (!detail) return;
        const id = Date.now() + Math.floor(Math.random() * 1000);
        setToasts((prev) => [...prev, { id, type: detail.type, data: safeParse(detail.data) }]);
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 3500);
      } catch (err) {
        console.warn('[RewardReveal] reward render failed:', err);
      }
    };
    window.addEventListener('gamenics:reward-reveal', handler as EventListener);
    return () => window.removeEventListener('gamenics:reward-reveal', handler as EventListener);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed right-4 bottom-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => {
        const p = presentFor(t);
        return (
          <div
            key={t.id}
            className="flex items-center gap-2.5 max-w-[320px] bg-surface border-2 border-ink rounded-pop-md shadow-hard px-4 py-3 font-display font-bold text-ink"
          >
            <span className={`w-8 h-8 rounded-full border-2 border-ink flex items-center justify-center flex-shrink-0 ${p.badgeCls}`}>
              <Icon name={p.icon} size={16} />
            </span>
            <span className="text-sm leading-snug">{p.text}</span>
          </div>
        );
      })}
    </div>
  );
};

export default RewardReveal;
